import { useEffect, useState } from 'react';
import api from './api.js';

/**
 * Runtime feature flags, served beside the brand by the PUBLIC GET /api/brand
 * (no session needed — the nav decides what to show before and after login).
 *
 * The contract is deliberately one-way: a flag only ever HIDES. Anything that
 * goes wrong — endpoint unreachable, older backend without `features`, a
 * mistyped value — fails OPEN, so a broken flag can never make a page vanish.
 * `isFeatureOn` therefore checks `!== false`, never `=== true`.
 *
 * One fetch per page load, shared by every subscriber (nav, sidebar, routes).
 */

let cached = null; // resolved features object once known
let inflight = null;
const listeners = new Set();

async function fetchFeatures() {
  try {
    // /brand has no { data } envelope, so api.request returns it whole. The
    // endpoint is public; the wrapper's token (when present) is harmless.
    const payload = await api.request('/brand');
    return payload?.features && typeof payload.features === 'object' ? payload.features : {};
  } catch {
    return {}; // fail open
  }
}

export function getFeatures() {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = fetchFeatures().then((features) => {
      cached = features;
      listeners.forEach((fn) => fn(features));
      return features;
    });
  }
  return inflight;
}

/** True unless the server said `false` for this flag — hide-only semantics. */
export function isFeatureOn(features, flag) {
  return !features || features[flag] !== false;
}

/**
 * The live features object for a component. Starts as `{}` (everything on)
 * and settles once the shared fetch resolves.
 */
export function useFeatures() {
  const [features, setFeatures] = useState(cached || {});
  useEffect(() => {
    let alive = true;
    const onChange = (f) => alive && setFeatures(f);
    listeners.add(onChange);
    getFeatures().then(onChange);
    return () => { alive = false; listeners.delete(onChange); };
  }, []);
  return features;
}

/** Which menu slugs are governed by which feature flag. */
export const FEATURE_SLUGS = Object.freeze({
  'talking-avatar': 'talkingAvatar',
});

/**
 * Drop feature-flagged entries from an (already role-filtered) menu tree.
 * Applied by the nav renderers rather than inside menuData so the static menu
 * definition stays data-only.
 */
export function pruneMenuForFeatures(nodes = [], features = {}) {
  return nodes.reduce((visible, node) => {
    const flag = node.slug ? FEATURE_SLUGS[node.slug] : null;
    if (flag && !isFeatureOn(features, flag)) return visible;
    if (node.children && node.children.length) {
      const children = pruneMenuForFeatures(node.children, features);
      if (children.length === 0) return visible;
      return [...visible, { ...node, children }];
    }
    return [...visible, node];
  }, []);
}

export default { getFeatures, useFeatures, isFeatureOn, pruneMenuForFeatures, FEATURE_SLUGS };
